import { Children, isValidElement } from "react";
import { ObjectUtils } from "primereact/utils";

// Texto plano de lo que devuelve un `body` (o de un `header`): strings y
// números tal cual, y de los elementos de React se toma el texto de sus
// children. Para los que no tienen children (ej. <Tag value="..." />) se usa
// `value` o `label`. Lo que no tiene texto (iconos, botones) queda en "".
export function textoDesdeNodo(nodo) {
  if (nodo === null || nodo === undefined || typeof nodo === "boolean") return "";
  if (typeof nodo === "string" || typeof nodo === "number") return String(nodo);
  if (nodo instanceof Date) return nodo.toLocaleDateString();
  if (Array.isArray(nodo)) {
    return nodo.map(textoDesdeNodo).filter(Boolean).join(" ");
  }
  if (isValidElement(nodo)) {
    const { children, value, label } = nodo.props;
    const texto = textoDesdeNodo(children);
    if (texto) return texto;
    return textoDesdeNodo(value ?? label);
  }
  return "";
}

// Columnas booleanas: en las tablas se muestran como un icono de check/cruz
// o un InputSwitch, que no tienen texto. Devuelve "Sí"/"No", o "" si el
// elemento no es ninguno de esos.
function textoBooleano(elemento) {
  const { className, checked } = elemento.props;
  if (typeof checked === "boolean") return checked ? "Sí" : "No";
  if (typeof className !== "string") return "";
  if (className.includes("pi-check")) return "Sí";
  if (className.includes("pi-times")) return "No";
  return "";
}

// Valor de una celda para el Excel. Los números se dejan como número para
// que Excel los trate como tal.
function valorCelda(valor) {
  if (typeof valor === "number") return valor;
  const texto = textoDesdeNodo(valor);
  if (texto === "" && isValidElement(valor)) return textoBooleano(valor);
  return texto;
}

// A partir de las <Column> hijas de una DataTable, deriva la lista de
// columnas exportables a Excel: solo las que tienen `header` (las de
// iconos/botones de acción, sin header, se ignoran). El valor de cada celda
// sale, en orden, de:
//   1. `exportValue` de la columna: (fila) => texto, para cuando el `body`
//      no se puede pasar a texto (componentes propios, iconos).
//   2. el texto de lo que renderiza `body`, que se llama igual que lo hace
//      PrimeReact: (fila, { rowIndex, field, column }).
//   3. `field`, resolviendo paths anidados ("norma.numero").
export function columnasExportablesDesde(children) {
  return Children.toArray(children)
    .filter(isValidElement)
    .map((child) => ({ child, header: textoDesdeNodo(child.props.header) }))
    .filter(({ header }) => header)
    .map(({ child, header }) => {
      const { field, body, exportValue } = child.props;
      const desdeField = (fila) =>
        field ? valorCelda(ObjectUtils.resolveFieldData(fila, field)) : "";

      return {
        header,
        field,
        value: (fila, rowIndex) => {
          if (exportValue) return valorCelda(exportValue(fila));
          if (typeof body !== "function") return desdeField(fila);
          try {
            const valor = valorCelda(body(fila, { rowIndex, field, column: child }));
            return valor === "" ? desdeField(fila) : valor;
          } catch {
            return desdeField(fila);
          }
        },
      };
    });
}
